import { sortByDate } from 'utils';
import actionTypes from '../actions/actionTypes';
import { mapAddress, updateDefaultAddress } from './utils';

const INITIAL_STATE = {
  data: null,
  error: false,
  loading: true,
  shippingLoading: false,
  shippingError: null,
  paymentsLoading: false,
  paymentsError: null,
  isChangingPayment: false,
  changingPaymentError: null,
  editPayment: false,
  profileData: null,
  createAccountLoading: false,
  updateAccountLoading: false,
  updateAccountError: null,
  notes: {
    data: [],
    error: false,
    loading: true,
    filter: 'allNotes',
    noteDetails: {
      data: null,
      loading: true,
    },
  },
  groups: {
    data: [],
    error: false,
    loading: true,
  },
  connects: {
    loading: false,
    data: [],
  },
  badges: [],
};

export const mapClientData = (payload) => {
  const { emailAddress, authenticationStatus, ...data } = payload;
  return {
    ...data,
    email: payload.email || payload.emailAddress,
    payments: payload.payments
      .filter((payment) => !!payment?.maskedNumber)
      .map((payment) => {
        return {
          ...payment,
          billingAddress: mapAddress(payment.billingAddress),
        };
      }),
    shippingAddress: payload.shippingAddress.map((address, index) => ({
      ...mapAddress(address),
      id: index.toString(),
    })),
  };
};

export const mapProfileData = (payload) => {
  // additionalEmail and additionalPhone are mapped according to requirements in
  // https://jira.mynmg.com/browse/OSP-1757?focusedCommentId=774663&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-774663
  const additionalEmail = payload.emails[0];
  let additionalPhone;
  let additionalPhoneType;

  if (payload.phones[0]) {
    additionalPhone = payload.phones[0].number;
    additionalPhoneType = payload.phones[0].type;
  }

  return {
    ...payload,
    additionalPhone,
    additionalPhoneType,
    additionalEmail,
    onlineProfiles: payload.onlineProfiles.map((profile) => {
      const { sourceName } = profile;

      return {
        ...profile,
        profileType: sourceName === 'NM' || sourceName === 'BG' ? `${sourceName}O` : sourceName,
      };
    }),
  };
};

export const addOnlineAccountToProfileData = (profileData, payload) => {
  const { sourceName } = payload;
  return {
    ...profileData,
    onlineProfiles: [
      ...profileData.onlineProfiles,
      {
        profileType: sourceName === 'NM' || sourceName === 'BG' ? `${sourceName}O` : sourceName,
        profileId: payload.profileId,
        email: payload.emailAddress,
        phone: payload.shippingAddress[0]?.dayTelephone,
        sourceName,
      },
    ],
  };
};

const getProfileDataForCreatedProfile = (profileData, newProfile) => {
  if (profileData) {
    return {
      ...profileData,
      onlineProfiles: [...profileData.onlineProfiles, newProfile],
    };
  }
  return null;
};

const addGroupToProfile = (profileGroups, newGroup) => {
  if (!profileGroups.some(({ groupId }) => groupId === newGroup.groupId)) {
    return [...profileGroups, newGroup];
  }

  return profileGroups;
};

export const getProfileDataForNoteDelete = (deletedNoteId, prevProfileDataState) => {
  if (!prevProfileDataState) {
    return prevProfileDataState;
  }

  return {
    ...prevProfileDataState,
    notes: {
      total: prevProfileDataState.notes.total - 1,
      notes: prevProfileDataState.notes.notes.filter((note) => note.id !== deletedNoteId),
    },
  };
};

export const clientDetails = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case actionTypes.CLIENT_DETAILS_LOADING: {
      return {
        ...state,
        data: null,
        error: false,
        loading: true,
      };
    }

    case actionTypes.CLIENT_DETAILS_SUCCESS: {
      return {
        ...state,
        data: mapClientData(action.payload),
        error: false,
        loading: false,
      };
    }

    case actionTypes.UPDATE_ONLINE_ACCOUNT_LOADING: {
      return {
        ...state,
        updateAccountLoading: true,
        updateAccountError: null,
      };
    }

    case actionTypes.UPDATE_ONLINE_ACCOUNT_SUCCESS: {
      return {
        ...state,
        data: mapClientData(action.payload),
        profileData: addOnlineAccountToProfileData(state.profileData, action.payload),
        updateAccountLoading: false,
        updateAccountError: action.payload,
      };
    }

    case actionTypes.UPDATE_ONLINE_ACCOUNT_FAILURE: {
      return {
        ...state,
        updateAccountLoading: false,
        updateAccountError: action.payload,
      };
    }

    case actionTypes.CLIENT_DETAILS_ERROR: {
      return {
        ...state,
        data: null,
        error: true,
        loading: false,
      };
    }

    case actionTypes.CMD_PROFILE_LOADING: {
      return {
        ...state,
        profileData: null,
        error: false,
        loading: true,
      };
    }

    case actionTypes.CMD_PROFILE_SUCCESS: {
      return {
        ...state,
        profileData: mapProfileData(action.payload),
        error: false,
        loading: false,
      };
    }

    case actionTypes.CMD_PROFILE_ERROR: {
      return {
        ...state,
        profileData: null,
        error: true,
        loading: false,
      };
    }

    case actionTypes.CLEAR_CMD_PROFILE_DATA: {
      return {
        ...state,
        profileData: null,
        error: false,
        loading: false,
      };
    }

    case actionTypes.EDIT_SHIPPING_SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          shippingAddress: state.data.shippingAddress.map((shippingAddress) =>
            shippingAddress.id === action.payload.id ? action.payload : shippingAddress,
          ),
        },
        shippingError: false,
        shippingLoading: false,
      };
    }

    case actionTypes.EDIT_SHIPPING_ERROR: {
      return {
        ...state,
        shippingError: true,
        shippingLoading: false,
      };
    }

    case actionTypes.EDIT_SHIPPING_LOADING: {
      return {
        ...state,
        shippingError: false,
        shippingLoading: true,
      };
    }

    case actionTypes.EDIT_PAYMENTS_SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          payments: state.data.payments.map((paymentItem) =>
            paymentItem.keyIdentifier === action.payload.keyIdentifier ? action.payload : paymentItem,
          ),
        },
        paymentsError: false,
        paymentsLoading: false,
      };
    }

    case actionTypes.EDIT_PAYMENTS_ERROR: {
      return {
        ...state,
        paymentsError: true,
        paymentsLoading: false,
      };
    }

    case actionTypes.EDIT_PAYMENTS_LOADING: {
      return {
        ...state,
        paymentsError: false,
        paymentsLoading: true,
      };
    }

    case actionTypes.GET_CLIENT_NOTES_SUCCESS:
      return {
        ...state,
        notes: {
          ...state.notes,
          data: sortByDate(action.payload),
          error: false,
          loading: false,
        },
      };

    case actionTypes.CREATE_CLIENT_NOTE_SUCCESS: {
      const profileData = state.profileData || { notes: {} };
      const currentProfileDataNotes = profileData.notes?.notes || [];
      const currentTotalCount = profileData.notes?.total || 0;

      profileData.notes.notes = sortByDate([...currentProfileDataNotes, action.payload]);
      profileData.notes.total = currentTotalCount + 1;

      return {
        ...state,
        notes: {
          ...state.notes,
          data: sortByDate([...state.notes.data, action.payload]),
          error: false,
          loading: false,
        },
        profileData,
      };
    }

    case actionTypes.EDIT_CLIENT_NOTE_SUCCESS:
      return {
        ...state,
        notes: {
          ...state.notes,
          noteDetails: {
            data: action.payload,
            loading: false,
          },
          error: false,
          loading: false,
        },
      };

    case actionTypes.DELETE_CLIENT_NOTES_SUCCESS:
      return {
        ...state,
        notes: {
          ...state.notes,
          data: state.notes.data.filter((note) => note.id !== action.payload),
          error: false,
          loading: false,
        },
        profileData: getProfileDataForNoteDelete(action.payload, state.profileData),
      };

    case actionTypes.GET_CLIENT_NOTE_DETAILS_SUCCESS:
      return {
        ...state,
        notes: {
          ...state.notes,
          noteDetails: {
            data: action.payload,
            loading: false,
          },
          error: false,
        },
      };

    case actionTypes.GET_CLIENT_NOTES_ERROR:
    case actionTypes.CREATE_CLIENT_NOTE_ERROR:
    case actionTypes.DELETE_CLIENT_NOTES_ERROR:
    case actionTypes.EDIT_CLIENT_NOTE_ERROR:
    case actionTypes.GET_CLIENT_NOTE_DETAILS_ERROR:
      return {
        ...state,
        notes: {
          ...state.notes,
          error: true,
          loading: false,
          noteDetails: {
            ...state.notes.noteDetails,
            loading: false,
          },
        },
      };

    case actionTypes.CREATE_CLIENT_NOTE_LOADING:
    case actionTypes.DELETE_CLIENT_NOTES_LOADING:
      return {
        ...state,
        notes: {
          ...state.notes,
          error: false,
          loading: true,
        },
      };

    case actionTypes.EDIT_CLIENT_NOTE_LOADING:
    case actionTypes.GET_CLIENT_NOTE_DETAILS_LOADING:
      return {
        ...state,
        notes: {
          ...state.notes,
          error: false,
          noteDetails: {
            ...state.notes.noteDetails,
            loading: true,
          },
        },
      };

    case actionTypes.GET_CLIENT_NOTES_LOADING:
      return {
        ...state,
        notes: {
          ...state.notes,
          data: [],
          error: false,
          loading: true,
        },
      };

    case actionTypes.NOTES_FILTER_BY:
      return {
        ...state,
        notes: {
          ...state.notes,
          filter: action.payload,
        },
      };

    case actionTypes.GET_CLIENT_GROUPS_SUCCESS:
      return {
        ...state,
        groups: {
          ...state.groups,
          data: action.payload,
          error: false,
          loading: false,
        },
      };

    case actionTypes.ADD_TO_GROUP_SUCCESS:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: false,
          error: false,
        },
      };

    case actionTypes.DELETE_FROM_GROUP_SUCCESS:
      return {
        ...state,
        groups: {
          ...state.groups,
          data: state.groups.data.filter((group) => !action.payload.groupId.includes(group.groupId)),
          loading: false,
          error: false,
        },
      };

    case actionTypes.GET_CLIENT_GROUPS_LOADING:
    case actionTypes.ADD_TO_GROUP_LOADING:
    case actionTypes.DELETE_FROM_GROUP_LOADING:
      return {
        ...state,
        groups: {
          ...state.groups,
          error: false,
          loading: true,
        },
      };

    case actionTypes.GET_CLIENT_GROUPS_ERROR:
    case actionTypes.ADD_TO_GROUP_ERROR:
    case actionTypes.DELETE_FROM_GROUP_ERROR:
      return {
        ...state,
        groups: {
          ...state.groups,
          error: true,
          loading: false,
        },
      };

    case actionTypes.SET_SHIPMENT_DEFAULT: {
      return {
        ...state,
        data: {
          ...state.data,
          shippingAddress: state.data.shippingAddress.map((address) => ({
            ...address,
            default: address.id === action.payload,
          })),
        },
      };
    }

    case actionTypes.SET_SHIPMENT_SELECTED: {
      return {
        ...state,
        data: {
          ...state.data,
          shippingAddress: state.data.shippingAddress.map((address) => ({
            ...address,
            selected: address.id === action.payload,
          })),
        },
      };
    }

    case actionTypes.CHANGE_PAYMENTS_LOADING:
      return {
        ...state,
        isChangingPayment: true,
        changingPaymentError: null,
      };

    case actionTypes.CHANGE_PAYMENTS_SUCCESS:
      return {
        ...state,
        isChangingPayment: false,
        changingPaymentError: null,
        editPayment: false,
        data: {
          ...state.data,
          payments: action.payload,
        },
      };

    case actionTypes.CHANGE_PAYMENTS_ERROR:
      return {
        ...state,
        isChangingPayment: false,
        changingPaymentError: action.payload,
      };

    case actionTypes.CHANGE_PAYMENTS_ERROR_RESET:
      return {
        ...state,
        changingPaymentError: null,
        error: false,
      };

    case actionTypes.ADD_SHIPPING_SUCCESS:
      return {
        ...state,
        shippingError: null,
        shippingLoading: false,
        data: {
          ...state.data,
          shippingAddress: updateDefaultAddress(state.data.shippingAddress, action.payload),
        },
      };

    case actionTypes.ADD_SHIPPING_LOADING:
      return {
        ...state,
        shippingLoading: true,
        shippingError: null,
      };

    case actionTypes.ADD_SHIPPING_ERROR:
      return {
        ...state,
        shippingError: action.payload,
        shippingLoading: false,
      };

    case actionTypes.CREATE_ONLINE_ACCOUNT_LOADING:
      return {
        ...state,
        createAccountLoading: true,
      };

    case actionTypes.CREATE_ONLINE_ACCOUNT_ERROR:
      return {
        ...state,
        createAccountLoading: false,
      };

    case actionTypes.CREATE_ONLINE_ACCOUNT_SUCCESS:
      return {
        ...state,
        createAccountLoading: false,
        profileData: getProfileDataForCreatedProfile(state.profileData, action.payload),
      };

    case actionTypes.ADD_GROUP_TO_PROFILE:
      return {
        ...state,
        groups: {
          ...state.groups,
          data: addGroupToProfile(state.groups.data, action.payload.group),
        },
      };
    case actionTypes.CLIENT_CONNECTS_LOADING:
      return {
        ...state,
        connects: {
          ...state.connects,
          loading: true,
        },
      };

    case actionTypes.SET_CLIENT_CONNECTS:
      return {
        ...state,
        connects: {
          loading: false,
          data: action.payload,
        },
      };

    case actionTypes.GET_CLIENT_BADGES_SUCCESS:
      return {
        ...state,
        badges: action.payload,
      };
    default:
      return state;
  }
};

export default clientDetails;
